import type { GetStaticProps } from "next"
import Link from "next/link"
import { useRouter } from "next/router"
import { useMemo } from "react"
import { twMerge } from "tailwind-merge"
import MarketingHeading from "@/components/common/MarketingHeading"
import { PageMeta } from "@/components/resources/components/Seo/PageMeta"
import { TeaserGrid } from "@/components/resources/components/TeaserGrid"
import { makeButtonClassName } from "@/components/resources/components/ui/Button"
import { getFeedTeasers } from "@/components/resources/data/feed"
import type { Teaser } from "@/components/resources/data/posts.types"
import { SiteLayout } from "@/components/resources/layouts/site"

interface Props {
  entries: Teaser[]
}

const PER_PAGE = 9

interface PaginationProps {
  page: number
  nextPage: number
  limit: number
  postsRemaining: boolean
}

const useRoutedPagination = <T,>(items: T[]): [T[], PaginationProps] => {
  const {
    query: { p },
  } = useRouter()
  const page = Number.parseInt((p && Array.isArray(p) ? p[0] : p) ?? "0", 10)
  const limit = page ? Math.min(items.length, PER_PAGE * (page + 1)) : PER_PAGE

  return [
    items.slice(0, limit),
    {
      page,
      nextPage: page + 1,
      limit,
      postsRemaining: items.length > limit,
    },
  ]
}

const ShowMore = ({ nextPage, postsRemaining, query }: PaginationProps & { query: string }) => {
  return (
    <Link
      href={`?q=${encodeURIComponent(query)}&p=${nextPage}`}
      scroll={false}
      shallow
      className={twMerge(
        makeButtonClassName("medium", "default", "neutral"),
        "mt-4",
        !postsRemaining ? "opacity-50 cursor-not-allowed pointer-events-none" : "",
      )}
      rel="next"
    >
      Load more
    </Link>
  )
}

const SearchResults = ({ entries }: Props) => {
  const router = useRouter()
  const query = (router.query.q as string) || ""

  const filteredEntries = useMemo(() => {
    if (!query.trim()) {
      return []
    }

    const searchTerm = query.toLowerCase()
    return entries.filter((entry) => {
      // Search in title and teaser text
      const titleMatch = entry.title.toLowerCase().includes(searchTerm)
      const teaserMatch = entry.teaserText?.toLowerCase().includes(searchTerm)

      return titleMatch || teaserMatch
    })
  }, [entries, query])

  const [shownPosts, pagination] = useRoutedPagination(filteredEntries)

  const resultCount = filteredEntries.length
  const title = query
    ? `${resultCount} result${resultCount !== 1 ? "s" : ""} for "${query}"`
    : "Search"

  return (
    <>
      <PageMeta
        meta={{
          title: `Search: ${query || ""}`,
          description: "Search Token Terminal blog posts and resources",
        }}
      />
      <article className="flex flex-col mb-16">
        <div className="max-w-[1250px] mx-auto w-full">
          <div className="py-8 md:py-12">
            <MarketingHeading level={1} className="text-3xl md:text-4xl mb-2">
              {title}
            </MarketingHeading>
            {!query.trim() && (
              <p className="text-fg-secondary">
                Enter a search term to find relevant articles and resources.
              </p>
            )}
          </div>

          {query.trim() &&
            (filteredEntries.length > 0 ? (
              <>
                <TeaserGrid teasers={shownPosts} />
                {filteredEntries.length > PER_PAGE ? (
                  <div className="pt-4 pb-8 flex justify-center">
                    <ShowMore {...pagination} query={query} />
                  </div>
                ) : null}
              </>
            ) : (
              <div className="py-12 text-center">
                <p className="text-fg-secondary">
                  No results found for &quot;{query}&quot;. Try a different search term.
                </p>
              </div>
            ))}
        </div>
      </article>
    </>
  )
}

SearchResults.getLayout = SiteLayout

export default SearchResults

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      entries: await getFeedTeasers(),
    },
    revalidate: 180,
  }
}
